import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, UserCheck, UserX, Trash2, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getPendingUsers()
      ]);
      setUsers(allRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId, approve) => {
    setActionLoading(true);
    try {
      await adminApi.approveUser(userId, approve);
      toast.success(approve ? 'Utente approvato' : 'Approvazione revocata');
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'operazione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await adminApi.deleteUser(selectedUser.id);
      toast.success('Utente eliminato');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-users-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight">
          Gestione Utenti
        </h1>
        <p className="text-slate-500 mt-1">
          Approva nuovi utenti e gestisci gli account esistenti
        </p>
      </div>

      {/* Pending Users */}
      {pendingUsers.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="pending-users-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope'] text-amber-800">
              <Clock size={20} />
              Utenti in Attesa di Approvazione ({pendingUsers.length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              Questi utenti hanno richiesto l'accesso al sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-amber-200 overflow-hidden bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-100">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Data Registrazione</TableHead>
                    <TableHead className="w-[200px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`pending-user-${user.id}`}>
                      <TableCell className="font-medium">{user.nome}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {format(new Date(user.created_at), 'dd MMM yyyy HH:mm', { locale: it })}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id, true)}
                            disabled={actionLoading}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-user-${user.id}`}
                          >
                            <UserCheck size={16} className="mr-1" />
                            Approva
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                            disabled={actionLoading}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Users */}
      <Card data-testid="all-users-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Users size={20} className="text-primary" />
            Tutti gli Utenti ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Ruolo</TableHead>
                    <TableHead className="font-semibold">Stato</TableHead>
                    <TableHead className="font-semibold">Data Registrazione</TableHead>
                    <TableHead className="w-[150px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {user.nome}
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs">Tu</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.ruolo === 'admin' ? (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Shield size={12} className="mr-1" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant="outline">Utente</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.approvato ? (
                          <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">In attesa</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: it })}
                      </TableCell>
                      <TableCell>
                        {user.id !== currentUser?.id && (
                          <div className="flex gap-2">
                            {user.approvato ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleApprove(user.id, false)}
                                disabled={actionLoading}
                                title="Revoca approvazione"
                              >
                                <UserX size={16} />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleApprove(user.id, true)}
                                disabled={actionLoading}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <UserCheck size={16} />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                              disabled={actionLoading}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600">Elimina Utente</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l'utente <strong>{selectedUser?.nome}</strong> ({selectedUser?.email})?
              <br />
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
